/**
 * `<ExprCell>` — compose an expression, emitting a `ScreenerExpr` directly.
 *
 * **Two ways in, one tree out.** The controls compose an AST with no parser at
 * all, so the builder works standalone; given a `compile` function the same cell
 * also accepts typed DSL. Both edit the same `ScreenerExpr`, so a column is never
 * "a text one" or "a structured one" — it is one column, editable either way.
 *
 * The text and the tree are not quite inverses, and the cell is honest about it:
 *
 * - Typing is authoritative when it compiles. The typed text is handed back as
 *   `source` and stored, so reopening shows what the user wrote.
 * - Editing a control reprints the text from the tree, because the control just
 *   changed what the expression *is*. `printExpr` emits the prefix spelling, so
 *   `c.sma20` reprints as `sma(close, 20)` — the same expression, spelled the
 *   way a printer can spell it. Reprinting is only triggered by a structural
 *   edit, so text a user typed is never rewritten under them mid-sentence.
 * - A failed compile changes nothing. The tree keeps its last good value and the
 *   error is shown against the input, so one bad keystroke cannot blank a column.
 *
 * The tree is edited in place by path, so the shape a user builds is exactly the
 * shape stored. No flattening, no normalisation that could lose a grouping the
 * user meant.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ScreenerExpr, ScreenerExprBinary } from "@qufolio/model";
import type { ScreenerColumnMeta } from "@qufolio/transport";

import { FieldRef, type FieldRefValue } from "./FieldRef.js";
import { SCALAR_FUNCTIONS, SERIES_FUNCTIONS, printExpr } from "../lib/expr.js";
import { DslInput, type DslInputHandle } from "./DslInput.js";
import { DslReference } from "./DslReference.js";
import type { DslCatalog } from "../lib/dsl-catalog.js";
import type {
  CompileExpr,
  DescribeColumn,
  DescribeExpr,
  TokenUsage,
} from "../lib/compile.js";

/** Everything callable, for highlighting. Both halves are legal everywhere. */
const ALL_FUNCTIONS = [...SCALAR_FUNCTIONS, ...SERIES_FUNCTIONS];

const ARITHMETIC_OPS = ["+", "-", "*", "/", "%"] as const;
const COMPARISON_OPS = ["==", "!=", ">", ">=", "<", "<="] as const;
const LOGICAL_OPS = ["and", "or"] as const;

export interface ExprCellProps {
  value: ScreenerExpr | undefined;
  meta: readonly ScreenerColumnMeta[];
  /**
   * `source` is the DSL text the user typed, present only when text authored the
   * expression. Callers persist it to `ScreenerComputed.source`; passing it back
   * is what makes reopening show the original spelling rather than a reprint.
   */
  onChange: (expr: ScreenerExpr, source?: string) => void;
  /** Show comparison and logical operators. Off for a value-producing column. */
  allowBoolean?: boolean;
  /**
   * Injected compiler (see `compile.ts`). Omitted, there is no text input and
   * this is the structured-only cell it has always been.
   */
  compile?: CompileExpr;
  /** The stored DSL text, so reopening seeds the input with what was typed. */
  source?: string;
  /**
   * The DSL vocabulary, for the reference panel. Injected like the compiler —
   * the island cannot fetch — and falls back to what it knows on its own.
   */
  catalog?: DslCatalog;
  /** English -> DSL. Adds a Describe mode; omitted, there simply isn't one. */
  describe?: DescribeExpr;
  /**
   * Columns the describer may reference, with their meanings and any the user
   * authored. Names alone are not a schema — `jforce2` is momentum and nothing
   * in the identifier says so.
   */
  describeColumns?: readonly DescribeColumn[];
}

/** A fresh literal, the neutral thing to put in an empty slot. */
const emptyExpr = (): ScreenerExpr => ({ kind: "literal", value: 0 });

/**
 * Replace the node at `path` (a list of "left"/"right"/arg indices).
 *
 * Structural sharing keeps every untouched branch identity-stable, so React
 * re-renders only the edited subtree.
 */
export function replaceAt(
  expr: ScreenerExpr,
  path: readonly (string | number)[],
  next: ScreenerExpr,
): ScreenerExpr {
  if (path.length === 0) return next;
  const [head, ...rest] = path;

  if (expr.kind === "binary" && (head === "left" || head === "right")) {
    return { ...expr, [head]: replaceAt(expr[head], rest, next) } as ScreenerExprBinary;
  }
  if (expr.kind === "call" && typeof head === "number") {
    const args = expr.args.slice();
    args[head] = replaceAt(args[head]!, rest, next);
    return { ...expr, args };
  }
  return expr;
}

export function ExprCell({
  value,
  meta,
  onChange,
  allowBoolean = false,
  compile,
  source,
  catalog,
  describe,
  describeColumns,
}: ExprCellProps) {
  const expr = value ?? emptyExpr();
  const printed = useMemo(() => printExpr(expr), [expr]);

  /**
   * Which editor is showing. Text when there is a compiler, because typing is
   * how anyone who knows the syntax writes an expression, and the structured
   * controls are then a fallback rather than the main road. Ephemeral (D8) — a
   * workspace stores the expression, never how it was last edited.
   */
  const [mode, setMode] = useState<Mode>(compile ? "text" : "builder");
  const [request, setRequest] = useState("");
  /** What the last describe cost, shown where the cost is incurred. */
  const [usage, setUsage] = useState<TokenUsage | null>(null);

  // Describe only appears when a describer was injected: it costs a model call
  // and a key, while compiling costs neither, so a host can have one without it.
  const modes: Mode[] = describe ? ["describe", "text", "builder"] : ["text", "builder"];
  /** Whether the reference is showing. Off by default; it is a lookup, not a
   *  permanent fixture, and the editor should not shrink for people who know
   *  the vocabulary. */
  const [showReference, setShowReference] = useState(false);
  const editorApi = useRef<DslInputHandle>(null);
  const [text, setText] = useState(source ?? printed);
  const [error, setError] = useState<{ message: string; position: number } | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * The last expression this cell emitted. An incoming `value` that is not it
   * came from elsewhere — a different column opened into the same cell — and the
   * input reseeds. Without this the input and the controls would overwrite each
   * other on every edit.
   */
  const emitted = useRef<ScreenerExpr | undefined>(value);

  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setText(source ?? printExpr(value ?? emptyExpr()));
    setError(null);
  }, [value, source]);

  const emit = (next: ScreenerExpr, nextSource?: string) => {
    emitted.current = next;
    onChange(next, nextSource);
  };

  // Scoping resolution to the screen's columns is what turns a typo into a named
  // compile error instead of a column of nulls. Empty metadata means the schema
  // is not known yet, and sending an empty list would reject every identifier —
  // so it is omitted and the server parses unchecked.
  const fields = useMemo(() => (meta.length ? meta.map((m) => m.field) : undefined), [meta]);

  /** Describe -> DSL -> the text editor, so the result is yours to edit. */
  const generate = async () => {
    if (!describe) return;
    const asked = request.trim();
    if (!asked) return;
    setBusy(true);
    try {
      // The current expression goes with it, so a second sentence refines
      // rather than restarts. `text` is what the editor holds, which is the
      // expression as it stands whether it was typed or generated.
      const result = await describe(asked, describeColumns, text.trim() || undefined);
      setUsage(result.usage ?? null);
      if (result.ok) {
        setError(null);
        setText(result.source);
        emit(result.expression, result.source);
        // The description is deliberately kept. It is the thing worth adding to
        // — clearing it would make every refinement start from a blank box.
        // Land in Text, not back here. The point is that you get an expression
        // you can read and correct — hiding it would make this a black box and
        // leave nothing to learn the syntax from.
        setMode("text");
      } else {
        setError(result.error);
      }
    } catch (cause) {
      setError({
        message: cause instanceof Error ? cause.message : "Could not reach QueVee.",
        position: -1,
      });
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!compile) return;
    const typed = text.trim();
    if (!typed) {
      setError({ message: "Empty expression", position: 0 });
      return;
    }
    setBusy(true);
    try {
      const result = await compile(typed, fields);
      if (result.ok) {
        setError(null);
        // The typed text is authoritative and travels with the tree, so the
        // stored column reopens spelled the way it was written.
        emit(result.expression, result.source);
      } else {
        // The tree keeps its last good value: one bad keystroke must not blank
        // a column that was working a moment ago.
        setError(result.error);
      }
    } catch (cause) {
      setError({
        message: cause instanceof Error ? cause.message : "Could not reach the compiler.",
        position: -1,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    // Column, filling whatever it is given: the controls take the height they
    // need at the top and the editor takes the rest. Typing is the main act
    // here, so it gets the space, not a single row under a stack of selects.
    <div style={cellLayout}>
      {compile && (
        <div style={editorLabelRow}>
          {modes.map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                ...modeButton,
                background: mode === m ? "var(--qf-selection)" : "transparent",
                color: mode === m ? "var(--qf-text)" : "var(--qf-text-secondary)",
              }}
            >
              {MODE_LABELS[m]}
            </button>
          ))}
          {mode !== "builder" && (
            <button
              onClick={() => setShowReference((open) => !open)}
              title="Functions and columns you can use"
              style={{
                ...modeButton,
                marginLeft: "auto",
                background: showReference ? "var(--qf-selection)" : "transparent",
                color: showReference ? "var(--qf-text)" : "var(--qf-text-secondary)",
              }}
            >
              Reference
            </button>
          )}
          <span
            style={{
              marginLeft: mode !== "builder" ? undefined : "auto",
              color: "var(--qf-text-muted)",
            }}
          >
            {mode === "text"
              ? "Enter applies · Shift+Enter new line · Esc reverts"
              : mode === "describe"
                ? "Say what the column should show; you get ScanScript you can edit"
                : "Compose without typing; the text updates to match"}
          </span>
        </div>
      )}

      {mode === "builder" && (
        <Node
          expr={expr}
          meta={meta}
          allowBoolean={allowBoolean}
          onChange={(next, path) => {
            const updated = replaceAt(expr, path, next);
            // A control edit changed what the expression *is*, so the text is
            // reprinted from the tree and the stored source dropped — it no
            // longer describes this expression.
            setText(printExpr(updated));
            setError(null);
            emit(updated);
          }}
          path={[]}
        />
      )}

      {mode === "describe" && describe && (
        <div style={editorColumn}>
          <textarea
            aria-label="Describe the column"
            value={request}
            placeholder={
              text.trim()
                ? "…and also above the 200 day — refines what you have"
                : "above its 50 day average on twice normal volume"
            }
            onChange={(e) => setRequest(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void generate();
              }
            }}
            style={describeBox}
          />
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <button onClick={() => void generate()} disabled={busy} style={generateButton}>
              {busy ? "Writing…" : "Generate expression"}
            </button>
            {error && (
              <span style={{ fontSize: "0.7rem", color: "var(--qf-negative)" }}>{error.message}</span>
            )}
            {usage && !error && (
              <span
                title={
                  usage.calls > 1
                    ? `${usage.calls} calls — the first answer did not compile`
                    : "One call"
                }
                style={{ fontSize: "0.68rem", color: "var(--qf-text-muted)" }}
              >
                {usage.inputTokens.toLocaleString()} in · {usage.outputTokens.toLocaleString()} out
                {usage.calls > 1 ? ` · ${usage.calls} calls` : ""}
              </span>
            )}
          </div>
          {showReference && (
            <DslReference
              catalog={catalog}
              meta={meta}
              onInsert={(t) => setRequest((r) => (r ? `${r} ${t}` : t))}
            />
          )}
        </div>
      )}

      {compile && mode === "text" && (
        <div style={editorColumn}>
          {busy && (
            <span style={{ fontSize: "0.66rem", color: "var(--qf-text-muted)" }}>compiling…</span>
          )}
          {showReference && (
            <DslReference
              catalog={catalog}
              meta={meta}
              onInsert={(t) => editorApi.current?.insert(t)}
            />
          )}
          {/*
            Compile on commit, not on every keystroke: a half-typed expression
            fails by definition, and flashing an error at someone mid-word is
            noise rather than feedback. The highlighting is live, though — that
            is the half that helps while typing.
          */}
          <DslInput
            ref={editorApi}
            value={text}
            onChange={setText}
            onSubmit={() => void submit()}
            onRevert={() => {
              setText(source ?? printed);
              setError(null);
            }}
            placeholder="c > c.sma20"
            errorAt={error?.position ?? -1}
            functions={ALL_FUNCTIONS}
            fields={fields}
            catalog={catalog}
          />
          {error && (
            <div style={{ fontSize: "0.7rem", color: "var(--qf-negative)" }}>
              {error.message}
              {error.position >= 0 && (
                <span style={{ opacity: 0.7 }}> (at {error.position + 1})</span>
              )}
            </div>
          )}
        </div>
      )}

      {!compile && (
        <code
          title="The ScanScript this expression prints as"
          style={{
            padding: "0.25rem 0.4rem",
            background: "var(--qf-background)",
            border: "1px solid var(--qf-border)",
            borderRadius: 4,
            color: "var(--qf-accent)",
            fontSize: "0.72rem",
            overflowX: "auto",
            whiteSpace: "nowrap",
          }}
        >
          {printed || "—"}
        </code>
      )}
    </div>
  );
}

interface NodeProps {
  expr: ScreenerExpr;
  meta: readonly ScreenerColumnMeta[];
  allowBoolean: boolean;
  path: (string | number)[];
  onChange: (next: ScreenerExpr, path: (string | number)[]) => void;
}

function Node({ expr, meta, allowBoolean, path, onChange }: NodeProps) {
  const set = (next: ScreenerExpr) => onChange(next, path);

  /** Change what *kind* of node this slot holds, keeping what can be kept. */
  const changeKind = (kind: string) => {
    if (kind === expr.kind) return;
    if (kind === "literal") set(emptyExpr());
    else if (kind === "field") set({ kind: "field", field: "" });
    else if (kind === "binary")
      set({ kind: "binary", op: "+", left: emptyExpr(), right: emptyExpr() });
    else if (kind === "call") set({ kind: "call", fn: "abs", args: [emptyExpr()] });
  };

  const ops = [
    ...ARITHMETIC_OPS,
    ...(allowBoolean ? [...COMPARISON_OPS, ...LOGICAL_OPS] : []),
  ];

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", flexWrap: "wrap" }}>
      <select
        // Position-aware, so a screen reader distinguishes the expression from
        // an argument inside it rather than announcing four identical selects.
        aria-label={path.length === 0 ? "Expression node kind" : `Argument kind ${path.join(".")}`}
        value={expr.kind}
        onChange={(e) => changeKind(e.target.value)}
        style={selectStyle}
      >
        <option value="literal">value</option>
        <option value="field">column</option>
        <option value="binary">a ∘ b</option>
        <option value="call">f(…)</option>
      </select>

      {expr.kind === "literal" && (
        <input
          value={String(expr.value ?? "")}
          onChange={(e) => {
            const raw = e.target.value;
            // A bare number stays a number so arithmetic works; anything else is
            // text, which is what a sector comparison needs.
            const n = Number(raw);
            set({ kind: "literal", value: raw !== "" && !Number.isNaN(n) ? n : raw });
          }}
          style={{ ...inputStyle, width: 90 }}
        />
      )}

      {expr.kind === "field" && (
        <FieldRef
          value={
            expr.field
              ? {
                  field: expr.field,
                  base: expr.ref?.base ?? expr.field,
                  frequency: expr.ref?.frequency ?? "",
                  offset: expr.ref?.offset ?? 0,
                }
              : undefined
          }
          meta={meta}
          onChange={(v: FieldRefValue) =>
            set({
              kind: "field",
              field: v.field,
              // Keeping the triple means this row reopens without re-parsing,
              // and a renamed field can be re-resolved from its parts.
              ref: { base: v.base, ...(v.frequency ? { frequency: v.frequency } : {}), offset: v.offset },
            })
          }
        />
      )}

      {expr.kind === "binary" && (
        <>
          <span style={groupStyle}>
            <Node
              expr={expr.left}
              meta={meta}
              allowBoolean={allowBoolean}
              path={[...path, "left"]}
              onChange={onChange}
            />
          </span>
          <select
            value={expr.op}
            onChange={(e) => set({ ...expr, op: e.target.value as ScreenerExprBinary["op"] })}
            style={selectStyle}
          >
            {ops.map((op) => (
              <option key={op} value={op}>
                {op}
              </option>
            ))}
          </select>
          <span style={groupStyle}>
            <Node
              expr={expr.right}
              meta={meta}
              allowBoolean={allowBoolean}
              path={[...path, "right"]}
              onChange={onChange}
            />
          </span>
        </>
      )}

      {expr.kind === "call" && (
        <>
          <select
            value={expr.fn}
            onChange={(e) => {
              const fn = e.target.value;
              // iff needs three arguments; the others take one or two. Pad
              // rather than truncate, so switching function never silently
              // discards a subexpression the user built.
              const wanted = fn === "iff" ? 3 : Math.max(1, expr.args.length);
              const args = [...expr.args];
              while (args.length < wanted) args.push(emptyExpr());
              set({ ...expr, fn, args });
            }}
            style={selectStyle}
          >
            {SCALAR_FUNCTIONS.map((fn) => (
              <option key={fn} value={fn}>
                {fn}
              </option>
            ))}
          </select>
          <span>(</span>
          {expr.args.map((arg, i) => (
            <span key={i} style={groupStyle}>
              <Node
                expr={arg}
                meta={meta}
                // iff's first argument is a condition, so booleans are legal
                // there even in a value-producing column.
                allowBoolean={allowBoolean || (expr.fn === "iff" && i === 0)}
                path={[...path, i]}
                onChange={onChange}
              />
              {i < expr.args.length - 1 && <span>,</span>}
            </span>
          ))}
          <span>)</span>
        </>
      )}
    </span>
  );
}

const cellLayout: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.35rem",
  height: "100%",
  minHeight: 0,
};

/** The editor takes every row the controls above did not. */
const editorColumn: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.2rem",
  flex: 1,
  minHeight: "4.5rem",
};

const editorLabelRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  fontSize: "0.66rem",
};

type Mode = "describe" | "text" | "builder";

const MODE_LABELS: Record<Mode, string> = {
  describe: "Describe",
  text: "Text",
  builder: "Builder",
};

const describeBox: React.CSSProperties = {
  flex: 1,
  minHeight: "3rem",
  resize: "none",
  padding: "0.3rem 0.4rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
  fontSize: "0.78rem",
  lineHeight: 1.5,
};

const generateButton: React.CSSProperties = {
  padding: "0.15rem 0.6rem",
  background: "var(--qf-accent)",
  border: "none",
  borderRadius: 4,
  color: "var(--qf-background)",
  fontSize: "0.72rem",
  fontWeight: 600,
  cursor: "pointer",
};

const modeButton: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0.1rem 0.5rem",
  fontSize: "0.7rem",
  cursor: "pointer",
};

const selectStyle: React.CSSProperties = {
  padding: "0.1rem 0.2rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
  fontSize: "0.74rem",
};

const inputStyle: React.CSSProperties = {
  padding: "0.15rem 0.3rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
  fontSize: "0.76rem",
};

const groupStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.2rem",
  padding: "0.05rem 0.15rem",
  border: "1px dashed var(--qf-border)",
  borderRadius: 4,
};
